// One overlapping court slot reported by the court-scheduling endpoints
// (create-with-courts / link), so the UI can render per-court conflicts like
// "Court 2 booked 18:30–19:30 (Reservation)" — backend 98e92fd + 87d4fa7.
export type CourtConflict = {
  courtId?: string
  startAt?: string
  endAt?: string
  slotType?: string
  programType?: string | null
  title?: string | null
}

// A concrete, safe failure reason an upstream provider surfaced (backend
// whitelists/caps it — never raw stacks or secrets). Currently the OpusClip
// clip-submit path (backend 670f9712) carries this on a failed submit body.
export type ApiErrorReason = {
  errorName?: string
  errorMessage?: string
  status?: number
}

export type ApiErrorPayload = {
  error?: string
  detail?: string
  code?: string
  error_code?: string
  // A machine-readable marker some endpoints put in the BODY rather than in
  // `code` — note this is the body's own string, never the HTTP status number
  // above. `players/updatePlayer` sends `"claimed_email_cannot_rotate"` here;
  // since backend fc6598d2 it sends the same marker in `code` as well, and the
  // backend drops this copy once no deployed client reads it. `playerEditRefusal`
  // therefore reads both, so that removal changes nothing on the screen.
  status?: string
  // Which unique claim a `code: "player_unique_claim_taken"` 409 collided on —
  // `"EMAIL"` or `"PHONE"`. One backend helper guards both with one sentence
  // shape, so this is the only thing that says which field the refusal is about
  // (backend fc6598d2). Without it a taken phone gets pinned to the email box.
  uniqueType?: string
  // Present on a `403 code: "follow_required"` body (the booking create, D2.2) — the
  // club's join policy, so the inline join step knows whether a self-join is possible
  // ("open") or the club adds members itself ("approval" / "closed").
  joinPolicy?: string
  // Which WEEKDAY a per-occurrence refusal is about, 0=Sun..6=Sat — sent with
  // `code: "recurrence_window_span"` once a series can carry a time per weekday, since
  // then only one of its rows is the 24-hour one. Without it the desk is told a series
  // is too long and left to find out which day; collapsing that to one generic sentence
  // is the failure this repo has now written down five times.
  day?: number
  // Present on a 409 court_conflict body (and reused by the link endpoint).
  conflicts?: CourtConflict[]
  // A ONE-court clash carries its row here, singular (`slotOverlapResult`), under the
  // same `court_conflict` code; `pages/organization/courtClash.ts` reads both shapes.
  conflict?: CourtConflict | null
  // The clubs a `409 code: "last_owner_of_club"` account deletion would strand — every
  // one of them, not the first (backend 28775a63, minted once in
  // `identity/softDeleteUser.mjs` and forwarded by all three delete doors). The remedy
  // is "hand this club on", which nobody can act on without knowing WHICH club, and
  // fixing one of three then retrying only earns the same refusal again. The names ride
  // in this FIELD and never inside the server's sentence, so the client composes its
  // own copy from them (`features/profile/deleteAccountRefusal.ts`).
  clubs?: { orgId?: string; name?: string }[]
  // Present on a failed clip submit — the concrete OpusClip reason (safe, capped).
  reason?: ApiErrorReason
  // Which sign-in account stands in the way of a `409
  // code: "destination_has_working_sign_in"` staff repair (backend 1684b7cc). The
  // one refusal on that surface that cannot be stated on the card in advance, so
  // the dialog has to be able to NAME the login it would have displaced and offer
  // Disconnect as the way through. `accountRef` is an opaque selector — never a
  // Cognito sub, never decoded, never stored.
  blockedBy?: { accountRef?: string; email?: string | null; provider?: string | null }
  // Which Cognito challenge a `501 challenge_not_supported` sign-in refusal is
  // about — e.g. "SOFTWARE_TOKEN_MFA" (backend 3487edf4). The name is a pool
  // configuration fact, not a fact about the account holder, so it leaks
  // nothing; without it every refused challenge has to share one vague
  // sentence that blames the product in an engineer's words.
  challenge?: string
  // Present on a `422 code: "booking_window_exceeded"` body (`advanceWindow.mjs`) — the
  // viewer's last bookable club-local day. `windowDays` + `opensOn` (the day the requested
  // date becomes bookable) let the client name a real date instead of echoing the server's
  // raw sentence (`features/bookings/windowRefusal.ts`); `maxDate` alone is the fallback
  // while the paired BE half is not yet deployed.
  maxDate?: string
  windowDays?: number
  opensOn?: string
  // Which record a `409 error_code: "target_cannot_hold_account"` adult create is about — `"new"`
  // (the body's date of birth is under 16) or `"existing"` (the email/phone matched a stored
  // under-16 or child record). Decides where the dialog shows it (`ownAccountRefusal.ts`).
  subject?: string
  // On a `402 court_cap_reached` body (`createCourt.mjs`): the owner's courts across ALL their
  // clubs and the plan's cap. Read only through `courtCapCountsOf` (labels.ts), which accepts
  // nothing but finite positive integers.
  courtCount?: unknown
  courtCap?: unknown
}

export class ApiError extends Error {
  status: number
  payload?: ApiErrorPayload

  constructor(status: number, message: string, payload?: ApiErrorPayload) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

export class UnauthorizedError extends ApiError {
  constructor(payload?: ApiErrorPayload) {
    super(401, payload?.error ?? 'Unauthorized', payload)
    this.name = 'UnauthorizedError'
  }
}

export class ForbiddenError extends ApiError {
  constructor(payload?: ApiErrorPayload) {
    super(403, payload?.error ?? 'Forbidden', payload)
    this.name = 'ForbiddenError'
  }
}

export function mapStatusToError(status: number, payload?: ApiErrorPayload): ApiError {
  switch (status) {
    case 401:
      return new UnauthorizedError(payload)
    case 403:
      return new ForbiddenError(payload)
    default:
      return new ApiError(status, payload?.error ?? `API request failed with status ${status}`, payload)
  }
}

export async function parseApiError(response: Response): Promise<ApiError> {
  let payload: ApiErrorPayload | undefined
  try {
    payload = (await response.json()) as ApiErrorPayload
  } catch {
    payload = undefined
  }
  return mapStatusToError(response.status, payload)
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}
